/* Matrix Distribution theme — lightweight vanilla JS, no dependencies. */
(() => {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const T = window.theme || { routes: {}, strings: {} };
  const root = (T.routes.root || '/').replace(/\/?$/, '/');

  const formatMoney = (cents) => {
    const fmt = T.moneyFormat || '£{{amount}}';
    const value = (cents / 100).toFixed(2);
    const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return fmt
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, value.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, withCommas);
  };

  const toast = (msg, isError = false) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = msg;
    el.classList.toggle('toast--error', isError);
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, 3200);
  };

  const fetchSections = async (sections, url = window.location.pathname) => {
    const sep = url.includes('?') ? '&' : '?';
    const res = await fetch(`${url}${sep}sections=${sections.join(',')}`);
    return res.json();
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ------------------------------------------------------------------
     Drawers (menu, cart, facets) with focus trap
     ------------------------------------------------------------------ */
  const overlay = $('[data-overlay]');
  let activeDrawer = null;
  let lastFocus = null;

  const focusables = (el) => $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', el)
    .filter((n) => n.offsetParent !== null || n === document.activeElement);

  function openDrawer(el, trigger) {
    if (!el) return;
    if (activeDrawer && activeDrawer !== el) closeDrawer(false);
    lastFocus = trigger || document.activeElement;
    activeDrawer = el;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    if (overlay) {
      overlay.hidden = false;
      requestAnimationFrame(() => overlay.classList.add('is-visible'));
    }
    document.body.classList.add('no-scroll');
    $$(`[aria-controls="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    setTimeout(() => (focusables(el)[0] || el).focus(), 60);
  }

  function closeDrawer(restoreFocus = true) {
    if (!activeDrawer) return;
    const el = activeDrawer;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    $$(`[aria-controls="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (overlay) {
      overlay.classList.remove('is-visible');
      setTimeout(() => { if (!activeDrawer) overlay.hidden = true; }, 250);
    }
    document.body.classList.remove('no-scroll');
    activeDrawer = null;
    if (restoreFocus && lastFocus) lastFocus.focus();
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if ($('.lightbox')) return;
      if (activeDrawer) closeDrawer();
      closeMegas();
    }
    if (e.key === 'Tab' && activeDrawer) {
      const f = focusables(activeDrawer);
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  overlay && overlay.addEventListener('click', () => closeDrawer());

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const name = opener.getAttribute('data-drawer-open');
      const el = $(`[data-drawer="${name}"]`);
      if (el) { e.preventDefault(); openDrawer(el, opener); }
      return;
    }
    if (e.target.closest('[data-drawer-close]')) { closeDrawer(); return; }
    if (e.target.closest('[data-facets-open]')) {
      const btn = e.target.closest('[data-facets-open]');
      openDrawer(document.getElementById(btn.getAttribute('aria-controls')), btn);
      return;
    }
    if (e.target.closest('[data-facets-close]')) { closeDrawer(); }
  });

  // Close mobile-only drawers when resizing to desktop
  const desktopMQ = window.matchMedia('(min-width: 990px)');
  desktopMQ.addEventListener('change', (mq) => {
    if (mq.matches && activeDrawer && (activeDrawer.matches('[data-drawer="menu"]') || activeDrawer.matches('.facets-panel'))) closeDrawer(false);
  });

  /* ------------------------------------------------------------------
     Header: sticky offset, scroll shadow, mega menu, mobile search
     ------------------------------------------------------------------ */
  const header = $('[data-header]');
  const setOffset = () => {
    if (!header) return;
    document.documentElement.style.setProperty('--sticky-offset', `${header.offsetHeight + 16}px`);
  };
  setOffset();
  window.addEventListener('resize', setOffset, { passive: true });
  window.addEventListener('scroll', () => {
    header && header.classList.toggle('is-scrolled', window.scrollY > 10);
  }, { passive: true });

  const navItems = $$('[data-nav-item]');
  function closeMegas(except) {
    navItems.forEach((item) => {
      if (item === except) return;
      item.classList.remove('is-open');
      const b = $('[data-nav-toggle]', item);
      b && b.setAttribute('aria-expanded', 'false');
    });
  }
  navItems.forEach((item) => {
    const btn = $('[data-nav-toggle]', item);
    let timer;
    const open = () => {
      clearTimeout(timer);
      closeMegas(item);
      item.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    };
    const close = () => {
      timer = setTimeout(() => {
        item.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
      }, 160);
    };
    btn.addEventListener('click', () => (item.classList.contains('is-open') ? (item.classList.remove('is-open'), btn.setAttribute('aria-expanded', 'false')) : open()));
    item.addEventListener('mouseenter', () => { if (desktopMQ.matches && window.matchMedia('(hover: hover)').matches) open(); });
    item.addEventListener('mouseleave', () => { if (desktopMQ.matches && window.matchMedia('(hover: hover)').matches) close(); });
    item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
  });
  document.addEventListener('click', (e) => { if (!e.target.closest('[data-nav-item]')) closeMegas(); });

  const searchToggle = $('[data-search-toggle]');
  const headerSearch = $('[data-header-search]');
  if (searchToggle && headerSearch) {
    searchToggle.addEventListener('click', () => {
      const open = headerSearch.classList.toggle('is-open');
      searchToggle.setAttribute('aria-expanded', String(open));
      if (open) $('input[type="search"]', headerSearch).focus();
      setOffset();
    });
  }

  // Mobile nav sub-menus
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-collapse-toggle]');
    if (!t) return;
    const target = document.getElementById(t.getAttribute('aria-controls'));
    const expanded = t.getAttribute('aria-expanded') === 'true';
    t.setAttribute('aria-expanded', String(!expanded));
    if (target) target.hidden = expanded;
  });

  // Announcement rotation (mobile)
  const ann = $('[data-announcement]');
  if (ann) {
    const items = $$('.announcement__item', ann);
    if (items.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      let i = 0;
      setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, 4000);
    }
  }

  /* ------------------------------------------------------------------
     Predictive search
     ------------------------------------------------------------------ */
  $$('[data-predictive-search]').forEach((form) => {
    const input = $('input[type="search"]', form);
    const results = $('[data-predictive-results]', form);
    if (!input || !results || !T.routes.predictiveSearch) return;
    let timer;
    let controller;
    const hide = () => { results.hidden = true; input.setAttribute('aria-expanded', 'false'); };
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { hide(); return; }
      timer = setTimeout(async () => {
        try {
          controller && controller.abort();
          controller = new AbortController();
          const url = `${T.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page&resources[limit]=6&resources[options][unavailable_products]=last&section_id=predictive-search`;
          const res = await fetch(url, { signal: controller.signal });
          if (!res.ok) throw new Error(res.status);
          const doc = parseHTML(await res.text());
          const content = $('#predictive-search-results', doc);
          results.innerHTML = content ? content.innerHTML : '';
          results.hidden = !content;
          input.setAttribute('aria-expanded', String(!!content));
        } catch (err) {
          if (err.name !== 'AbortError') hide();
        }
      }, 220);
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { hide(); }
      if (e.key === 'ArrowDown' && !results.hidden) {
        const first = $('a', results);
        if (first) { e.preventDefault(); first.focus(); }
      }
    });
    results.addEventListener('keydown', (e) => {
      const links = $$('a', results);
      const idx = links.indexOf(document.activeElement);
      if (e.key === 'ArrowDown' && idx < links.length - 1) { e.preventDefault(); links[idx + 1].focus(); }
      if (e.key === 'ArrowUp') { e.preventDefault(); (idx > 0 ? links[idx - 1] : input).focus(); }
      if (e.key === 'Escape') { hide(); input.focus(); }
    });
    document.addEventListener('click', (e) => { if (!form.contains(e.target)) hide(); });
  });

  /* ------------------------------------------------------------------
     Quantity inputs
     ------------------------------------------------------------------ */
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-qty-minus]');
    const plus = e.target.closest('[data-qty-plus]');
    if (!minus && !plus) return;
    const wrap = e.target.closest('[data-qty]');
    const input = $('input', wrap);
    const min = parseInt(input.min || '0', 10);
    const max = input.max ? parseInt(input.max, 10) : Infinity;
    let v = parseInt(input.value || '0', 10) + (plus ? 1 : -1);
    v = Math.max(min, Math.min(max, v));
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ------------------------------------------------------------------
     Cart
     ------------------------------------------------------------------ */
  const cartDrawer = $('[data-drawer="cart"]');

  function updateCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      if (el.id === 'CartDrawerInner') return;
      el.dataset.count = count;
      el.textContent = count > 0 ? count : '';
    });
  }

  async function refreshCart() {
    const ids = [];
    if (cartDrawer) ids.push('cart-drawer');
    const cartPage = $('[data-cart-page]');
    const pageSection = cartPage && cartPage.closest('[id^="shopify-section-"]');
    if (pageSection) ids.push(pageSection.id.replace('shopify-section-', ''));
    let count = null;
    if (ids.length) {
      const data = await fetchSections(ids, T.routes.cart || '/cart');
      if (cartDrawer && data['cart-drawer']) {
        const doc = parseHTML(data['cart-drawer']);
        const inner = $('#CartDrawerInner', doc);
        if (inner) {
          $('#CartDrawerInner').replaceWith(inner);
          count = parseInt(inner.dataset.cartCount, 10);
        }
      }
      if (pageSection && data[ids[ids.length - 1]]) {
        const doc = parseHTML(data[ids[ids.length - 1]]);
        const fresh = $('[data-cart-page]', doc);
        if (fresh) cartPage.replaceWith(fresh);
      }
    }
    if (count === null || Number.isNaN(count)) {
      const cart = await (await fetch(`${root}cart.js`)).json();
      count = cart.item_count;
    }
    updateCount(count);
  }

  async function changeLine(line, quantity, el) {
    const row = el && el.closest('[data-cart-item]');
    row && row.classList.add('is-loading');
    try {
      const res = await fetch(`${T.routes.cartChange || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: parseInt(line, 10), quantity })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || T.strings.cartError);
      await refreshCart();
    } catch (err) {
      row && row.classList.remove('is-loading');
      toast(err.message || T.strings.cartError, true);
      await refreshCart();
    }
  }

  document.addEventListener('change', (e) => {
    const input = e.target.closest('input[data-line]');
    if (input) {
      clearTimeout(input._t);
      input._t = setTimeout(() => changeLine(input.dataset.line, Math.max(0, parseInt(input.value || '0', 10)), input), 350);
    }
    const note = e.target.closest('[data-cart-note]');
    if (note) {
      fetch(`${T.routes.cartUpdate || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.value })
      });
    }
  });

  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-cart-remove]');
    if (!rm) return;
    e.preventDefault();
    changeLine(rm.getAttribute('data-cart-remove'), 0, rm);
  });

  async function addToCart(form, button) {
    const errorBox = $('[data-form-error]', form);
    if (errorBox) errorBox.hidden = true;
    button && button.classList.add('is-loading');
    button && button.setAttribute('aria-busy', 'true');
    try {
      const fd = new FormData(form);
      const res = await fetch(`${T.routes.cartAdd || '/cart/add'}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || T.strings.cartError);
      await refreshCart();
      if (cartDrawer) openDrawer(cartDrawer, button);
      else toast(T.strings.added);
    } catch (err) {
      if (errorBox) { errorBox.textContent = err.message; errorBox.hidden = false; }
      else toast(err.message, true);
    } finally {
      button && button.classList.remove('is-loading');
      button && button.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form || T.cartType !== 'drawer') return;
    e.preventDefault();
    addToCart(form, e.submitter || $('[type="submit"]', form));
  });

  /* ------------------------------------------------------------------
     Product page
     ------------------------------------------------------------------ */
  $$('[data-product-section]').forEach((section) => {
    const jsonEl = $('[data-product-json]', section);
    const product = jsonEl ? JSON.parse(jsonEl.textContent) : null;
    const form = $('[data-product-form]', section);
    const idInput = $('[data-variant-id]', section);
    const addBtn = $('[data-add-button]', section);
    const stickyBar = $('[data-sticky-atc]', section);
    const stickyBtn = $('[data-sticky-add]', section);
    const quoteLink = $('[data-quote-link]', section);

    /* Gallery */
    const gallery = $('[data-gallery]', section);
    const slides = gallery && $('[data-slides]', gallery);
    const slideEls = slides ? $$('[data-slide]', slides) : [];
    const thumbs = gallery ? $$('[data-thumb]', gallery) : [];
    const dots = gallery ? $$('[data-dot]', gallery) : [];
    const prev = gallery && $('[data-gallery-prev]', gallery);
    const next = gallery && $('[data-gallery-next]', gallery);
    let current = 0;

    const setActive = (i) => {
      current = i;
      thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === i)));
      dots.forEach((d, n) => d.setAttribute('aria-current', String(n === i)));
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slideEls.length - 1;
      const thumb = thumbs[i];
      if (thumb && thumb.parentElement.parentElement.scrollHeight > thumb.parentElement.parentElement.clientHeight) {
        thumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    };
    const goTo = (i, smooth = true) => {
      if (!slides || !slideEls[i]) return;
      slides.scrollTo({ left: slideEls[i].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      setActive(i);
    };
    if (slides) {
      let st;
      slides.addEventListener('scroll', () => {
        clearTimeout(st);
        st = setTimeout(() => {
          const i = Math.round(slides.scrollLeft / slides.clientWidth);
          if (i !== current) setActive(i);
        }, 60);
      }, { passive: true });
    }
    thumbs.forEach((t) => t.addEventListener('click', () => goTo(parseInt(t.dataset.thumb, 10))));
    dots.forEach((d) => d.addEventListener('click', () => goTo(parseInt(d.dataset.dot, 10))));
    prev && prev.addEventListener('click', () => goTo(Math.max(0, current - 1)));
    next && next.addEventListener('click', () => goTo(Math.min(slideEls.length - 1, current + 1)));

    // Hover zoom (desktop pointer)
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      $$('.gallery__zoom', section).forEach((btn) => {
        const img = $('img', btn);
        if (!img) return;
        btn.addEventListener('mousemove', (e) => {
          const r = btn.getBoundingClientRect();
          img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
          const base = btn.closest('.gallery__slide--zoom') ? parseFloat(getComputedStyle(btn).getPropertyValue('--card-zoom')) || 2.6 : 1;
          img.style.transform = `scale(${(base * 1.8).toFixed(2)})`;
        });
        btn.addEventListener('mouseleave', () => { img.style.transform = ''; });
      });
    }

    // Lightbox
    const images = $$('.gallery__zoom img', section).map((img) => ({ src: img.dataset.full || img.currentSrc || img.src, alt: img.alt }));
    $$('[data-zoom]', section).forEach((btn, idx) => btn.addEventListener('click', () => openLightbox(images, idx, btn)));

    /* Variants */
    const getSelectedOptions = () => {
      const picker = $('[data-variant-picker]', section);
      if (!picker || !product) return null;
      const opts = [];
      product.options.forEach((_, i) => {
        const checked = $(`input[data-option-index="${i}"]:checked`, picker);
        const select = $(`select[data-option-index="${i}"]`, picker);
        opts[i] = checked ? checked.value : select ? select.value : null;
      });
      return opts;
    };

    const updateAvailability = (selected) => {
      const picker = $('[data-variant-picker]', section);
      if (!picker || !product) return;
      product.options.forEach((_, i) => {
        $$(`input[data-option-index="${i}"]`, picker).forEach((input) => {
          const test = selected.slice();
          test[i] = input.value;
          const match = product.variants.find((v) => v.options.every((o, n) => o === test[n]));
          input.classList.toggle('is-unavailable', !match || !match.available);
        });
        const label = $(`[data-selected-option="${i}"]`, picker);
        if (label) label.textContent = selected[i];
      });
    };

    const renderVariant = (variant) => {
      const priceWrap = $('[data-product-price]', section);
      const stockWrap = $('[data-product-stock]', section);
      const stickyPrice = $('[data-sticky-price]', section);
      const priceHTML = variant
        ? `<div class="price price--large" data-price><span class="price__current">${variant.price_html}</span>${variant.compare_at_price > variant.price ? `<s class="price__compare">${variant.compare_html}</s>` : ''}</div>`
        : '';
      if (priceWrap && variant) priceWrap.innerHTML = priceHTML;
      if (stickyPrice && variant) stickyPrice.innerHTML = priceHTML.replace(' price--large', '');
      if (stockWrap) {
        let cls = 'stock stock--out';
        let txt = T.strings.unavailable;
        if (variant) {
          if (variant.available) {
            cls = variant.low_stock ? 'stock stock--low' : 'stock';
            txt = variant.low_stock ? T.strings.lowStock : T.strings.inStock;
          } else txt = T.strings.soldOut;
        }
        stockWrap.innerHTML = `<span class="${cls}" data-stock>${txt}</span>`;
      }
      const can = !!(variant && variant.available);
      [addBtn, stickyBtn].forEach((b) => {
        if (!b) return;
        b.disabled = !can;
        const icon = b === addBtn && can ? (b.querySelector('svg') ? b.querySelector('svg').outerHTML : '') : '';
        b.innerHTML = icon + (can ? T.strings.addToCart : variant ? T.strings.soldOut : T.strings.unavailable);
      });
      if (variant) {
        if (idInput) idInput.value = variant.id;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        if (quoteLink) {
          const base = quoteLink.dataset.quoteBase || '/pages/trade-enquiries';
          quoteLink.href = `${base}${base.includes('?') ? '&' : '?'}product=${encodeURIComponent(product.handle)}&variant=${variant.id}#quote`;
        }
        if (variant.media_id) {
          const idx = slideEls.findIndex((s) => s.dataset.mediaId === String(variant.media_id));
          if (idx > -1) goTo(idx);
        }
      }
    };

    section.addEventListener('change', (e) => {
      if (!e.target.matches('[data-option-index]') || !product) return;
      const selected = getSelectedOptions();
      const variant = product.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      updateAvailability(selected);
      renderVariant(variant);
    });

    /* Sticky ATC */
    if (stickyBar && form) {
      const target = $('.buy', form) || form;
      const io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        stickyBar.classList.toggle('is-visible', show);
        stickyBar.setAttribute('aria-hidden', String(!show));
        stickyBtn && (stickyBtn.tabIndex = show ? 0 : -1);
      });
      io.observe(target);
      stickyBtn && stickyBtn.addEventListener('click', () => {
        if (T.cartType === 'drawer') addToCart(form, stickyBtn);
        else form.requestSubmit ? form.requestSubmit() : form.submit();
      });
    }

    /* Tabs */
    const tabs = $('[data-tabs]', section);
    if (tabs) {
      const btns = $$('[data-tab]', tabs);
      const panels = $$('[data-panel]', tabs);
      const activate = (name, focus) => {
        btns.forEach((b) => {
          const on = b.dataset.tab === name;
          b.setAttribute('aria-selected', String(on));
          b.tabIndex = on ? 0 : -1;
          if (on && focus) b.focus();
        });
        panels.forEach((p) => {
          const on = p.dataset.panel === name;
          p.classList.toggle('is-active', on);
          if (desktopMQ.matches) p.open = true;
        });
      };
      btns.forEach((b, i) => {
        b.addEventListener('click', () => activate(b.dataset.tab));
        b.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') activate(btns[(i + 1) % btns.length].dataset.tab, true);
          if (e.key === 'ArrowLeft') activate(btns[(i - 1 + btns.length) % btns.length].dataset.tab, true);
        });
      });
      if (desktopMQ.matches) panels.forEach((p) => { p.open = true; });
      $$('a[href="#ProductTab-reviews"]', section).forEach((a) => a.addEventListener('click', () => activate('reviews')));
    }

    /* Share */
    const share = $('[data-share]', section);
    share && share.addEventListener('click', async () => {
      const data = { title: share.dataset.shareTitle, url: share.dataset.shareUrl };
      if (navigator.share) { try { await navigator.share(data); } catch (e) { /* cancelled */ } }
      else if (navigator.clipboard) { await navigator.clipboard.writeText(data.url); toast('Link copied'); }
    });
  });

  /* ------------------------------------------------------------------
     Lightbox
     ------------------------------------------------------------------ */
  function openLightbox(images, index, trigger) {
    if (!images.length) return;
    let i = index;
    const box = document.createElement('div');
    box.className = 'lightbox';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'Image viewer');
    box.innerHTML = `
      <span class="lightbox__counter"></span>
      <button type="button" class="lightbox__close" aria-label="Close"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 5l14 14M19 5 5 19"/></svg></button>
      ${images.length > 1 ? '<button type="button" class="lightbox__nav lightbox__nav--prev" aria-label="Previous"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="m15 5-7 7 7 7"/></svg></button><button type="button" class="lightbox__nav lightbox__nav--next" aria-label="Next"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="m9 5 7 7-7 7"/></svg></button>' : ''}
      <div class="lightbox__img-wrap"><img class="lightbox__img" alt=""></div>`;
    document.body.appendChild(box);
    document.body.classList.add('no-scroll');
    const img = $('.lightbox__img', box);
    const counter = $('.lightbox__counter', box);
    const show = () => {
      img.classList.remove('is-zoomed');
      img.src = images[i].src;
      img.alt = images[i].alt || '';
      counter.textContent = images.length > 1 ? `${i + 1} / ${images.length}` : '';
    };
    const close = () => {
      box.remove();
      document.body.classList.remove('no-scroll');
      document.removeEventListener('keydown', onKey);
      trigger && trigger.focus();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') { i = (i + 1) % images.length; show(); }
      if (e.key === 'ArrowLeft') { i = (i - 1 + images.length) % images.length; show(); }
    };
    document.addEventListener('keydown', onKey);
    $('.lightbox__close', box).addEventListener('click', close);
    const p = $('.lightbox__nav--prev', box);
    const n = $('.lightbox__nav--next', box);
    p && p.addEventListener('click', () => { i = (i - 1 + images.length) % images.length; show(); });
    n && n.addEventListener('click', () => { i = (i + 1) % images.length; show(); });
    img.addEventListener('click', () => img.classList.toggle('is-zoomed'));
    box.addEventListener('click', (e) => { if (e.target === box || e.target.classList.contains('lightbox__img-wrap')) close(); });
    show();
    $('.lightbox__close', box).focus();
  }

  /* ------------------------------------------------------------------
     Collection / search filtering (Section Rendering API)
     ------------------------------------------------------------------ */
  $$('[data-collection-section]').forEach((section) => {
    const sectionId = section.dataset.sectionId;
    let controller;

    const render = async (url, push = true) => {
      const results = $('[data-results]', section);
      results && results.classList.add('is-loading');
      try {
        controller && controller.abort();
        controller = new AbortController();
        const u = new URL(url, window.location.origin);
        u.searchParams.set('section_id', sectionId);
        const res = await fetch(u.toString(), { signal: controller.signal });
        const doc = parseHTML(await res.text());
        $$('[data-ajax-region]', section).forEach((region) => {
          const name = region.dataset.ajaxRegion;
          const fresh = $(`[data-ajax-region="${name}"]`, doc);
          if (!fresh) return;
          if (name === 'facets') {
            // Preserve open/closed state of filter groups
            const openState = $$('details', region).map((d) => d.open);
            region.innerHTML = fresh.innerHTML;
            $$('details', region).forEach((d, i) => { if (openState[i] !== undefined) d.open = openState[i]; });
          } else {
            region.innerHTML = fresh.innerHTML;
          }
        });
        u.searchParams.delete('section_id');
        if (push) window.history.pushState({ facets: true }, '', u.pathname + u.search);
      } catch (err) {
        if (err.name !== 'AbortError') window.location.href = url;
      } finally {
        const r = $('[data-results]', section);
        r && r.classList.remove('is-loading');
      }
    };

    const formURL = () => {
      const form = $('[data-facet-form]', section);
      const params = new URLSearchParams();
      if (form) {
        new FormData(form).forEach((v, k) => { if (v !== '') params.append(k, v); });
      }
      const sort = $('[data-sort]', section);
      if (sort && !params.has('sort_by')) params.set('sort_by', sort.value);
      return `${window.location.pathname}?${params.toString()}`;
    };

    section.addEventListener('change', (e) => {
      if (e.target.closest('[data-facet-form]') || e.target.matches('[data-sort]')) {
        clearTimeout(section._t);
        const delay = e.target.type === 'number' ? 600 : 0;
        section._t = setTimeout(() => render(formURL()), delay);
      }
    });
    section.addEventListener('submit', (e) => {
      if (e.target.closest('[data-facet-form]')) { e.preventDefault(); render(formURL()); }
    });
    section.addEventListener('click', (e) => {
      const link = e.target.closest('[data-facet-link]');
      if (link) { e.preventDefault(); render(link.href); return; }

      const more = e.target.closest('[data-load-more-btn]');
      if (more) {
        e.preventDefault();
        more.classList.add('is-loading');
        const u = new URL(more.href, window.location.origin);
        u.searchParams.set('section_id', sectionId);
        fetch(u.toString()).then((r) => r.text()).then((html) => {
          const doc = parseHTML(html);
          const grid = $('[data-product-grid]', section);
          const freshGrid = $('[data-product-grid]', doc);
          if (grid && freshGrid) $$(':scope > li', freshGrid).forEach((li) => grid.appendChild(li));
          const wrap = $('[data-load-more]', section);
          const freshWrap = $('[data-load-more]', doc);
          if (wrap) { if (freshWrap) wrap.replaceWith(freshWrap); else wrap.remove(); }
        }).catch(() => { window.location.href = more.href; });
      }
    });
    window.addEventListener('popstate', (e) => { if (e.state && e.state.facets) render(window.location.href, false); });
  });

  /* Collection description "Read more" (mobile) */
  $$('[data-readmore]').forEach((text) => {
    const btn = text.parentElement.querySelector('[data-readmore-btn]');
    if (!btn) return;
    const label = btn.textContent;
    const check = () => { if (!text.classList.contains('is-expanded')) btn.hidden = text.scrollHeight <= text.clientHeight + 2; };
    check();
    window.addEventListener('resize', check, { passive: true });
    btn.addEventListener('click', () => {
      const open = text.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? (T.strings.readLess || 'Read less') : label;
    });
  });

  /* Footer columns: collapsed on mobile, always open on desktop */
  const footerMQ = window.matchMedia('(min-width: 750px)');
  const syncFooter = () => $$('[data-footer-acc]').forEach((d) => { d.open = footerMQ.matches; });
  syncFooter();
  footerMQ.addEventListener('change', syncFooter);

  /* Delivery info cards: collapsed rows on mobile, always open on desktop */
  const syncCards = () => $$('.dcard').forEach((d) => { d.open = desktopMQ.matches; });
  syncCards();
  desktopMQ.addEventListener('change', syncCards);

  /* ------------------------------------------------------------------
     Product recommendations
     ------------------------------------------------------------------ */
  $$('[data-recommendations]').forEach((el) => {
    const load = () => fetch(el.dataset.url).then((r) => r.text()).then((html) => {
      const fresh = $('[data-recommendations]', parseHTML(html));
      if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
      else el.remove();
    }).catch(() => el.remove());
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) { io.disconnect(); load(); }
      }, { rootMargin: '400px 0px' });
      io.observe(el);
    } else load();
  });

  /* ------------------------------------------------------------------
     Quote form: carry product details from product pages
     ------------------------------------------------------------------ */
  const quote = $('[data-quote-form]');
  if (quote) {
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('product');
    const variantId = params.get('variant');
    if (handle && /^[a-z0-9-]+$/i.test(handle)) {
      fetch(`${root}products/${handle}.js`).then((r) => (r.ok ? r.json() : null)).then((p) => {
        if (!p) return;
        const v = (variantId && p.variants.find((x) => String(x.id) === variantId)) || p.variants[0];
        const title = v && v.title !== 'Default Title' ? `${p.title} – ${v.title}` : p.title;
        const box = $('[data-quote-product]', quote);
        $('[data-quote-product-title]', quote).textContent = title;
        const img = $('[data-quote-product-img]', quote);
        const src = (v && v.featured_image && v.featured_image.src) || p.featured_image;
        if (img && src) { img.src = `${src}${src.includes('?') ? '&' : '?'}width=120`; img.hidden = false; }
        box.hidden = false;
        const urlField = $('[data-quote-product-url]', quote);
        if (urlField) urlField.value = `${window.location.origin}${root}products/${p.handle}${v ? `?variant=${v.id}` : ''}`;
        const req = $('[data-quote-requirements]', quote);
        if (req && !req.value) req.value = `${title}\n`;
      }).catch(() => {});
    }
  }
})();
